import React, { useState } from 'react';
import { Search, Calendar, Phone, Hash, Globe, Smartphone, ChevronRight, Filter } from 'lucide-react';
import { Order, OrderStatus } from './OrderQueue';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface OrderHistoryProps {
  orders: Order[];
  onSelectOrder: (order: Order) => void;
}
export function OrderHistory({
  orders,
  onSelectOrder
}: OrderHistoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneQuery, setPhoneQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const filteredOrders = orders.filter(order => {
    const isTerminalStatus = ['Delivered', 'Cancelled', 'Rejected', 'Reassigned', 'Return Requested', 'Refunded', 'Returned to Stock'].includes(order.status);
    if (!isTerminalStatus) return false;
    const matchesId = order.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhone = order.customer.mobile.includes(phoneQuery) || !phoneQuery;
    const matchesSource = sourceFilter === 'all' || order.channel === sourceFilter;
    return matchesId && matchesPhone && matchesSource;
  });
  return <div className="space-y-6" data-id="element-631" data-mp="2jqbi5r0xl6t8">
      <div className="flex items-center justify-between" data-id="element-632" data-mp="2jpjzfgsfn3kd">
        <div data-id="element-633" data-mp="25qslbudklhw8">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-634" data-mp="25qahocokw1ea">Order History</h2>
          <p className="text-slate-500" data-id="element-635" data-mp="2aq55u8gyycvn">Search and review past order logs</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-4 gap-4" data-id="element-636" data-mp="1wrp1go6kwygo">
        <div className="space-y-2" data-id="element-637" data-mp="20t0iw6faudds">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-638" data-mp="20u27zet5h0ei">
            <Hash className="w-3 h-3" data-id="element-639" data-mp="3v69vi7356f09" />
            Order ID
          </label>
          <div className="relative" data-id="element-640" data-mp="3v45ubwwmnu3o">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-641" data-mp="17yc7bg0g983t" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search ID..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-642" data-mp="12y80yit99trb" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-643" data-mp="1mm70exiddehf">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-644" data-mp="1mo0bcp00mkv8">
            <Phone className="w-3 h-3" data-id="element-645" data-mp="1hu5t7q026dy9" />
            Customer Phone
          </label>
          <div className="relative" data-id="element-646" data-mp="1cw468tzihuhi">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-647" data-mp="1cv2usoel35z9" />
            <input type="text" value={phoneQuery} onChange={e => setPhoneQuery(e.target.value)} placeholder="050..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-648" data-mp="2ae4e6u5dqgl6" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-649" data-mp="2mwzvqieux5c6">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-650" data-mp="2qx0egf8zo2p1">
            <Globe className="w-3 h-3" data-id="element-651" data-mp="2vuk1o4nnzpt4" />
            Source
          </label>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2 appearance-none" data-id="element-652" data-mp="30uepfzc57z9y">
            <option value="all" data-id="element-653" data-mp="3eml6dnn4eaag">All Sources</option>
            <option value="Website" data-id="element-654" data-mp="3enw8lj0x39zg">Website</option>
            <option value="Aggregator" data-id="element-655" data-mp="3jkee4l42nccu">Aggregator</option>
            <option value="Yaser eRx" data-id="element-656" data-mp="3jitist9xnpdx">Yaser eRx</option>
            <option value="Walk-in" data-id="element-657" data-mp="35xe0zfb7tvm3">Walk-in</option>
            <option value="Insurance Platform" data-id="element-658" data-mp="31wu1w6730hxb">Insurance Platform</option>
          </select>
        </div>

        <div className="space-y-2" data-id="element-659" data-mp="3fo8u93mvpvh1">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-660" data-mp="3fn79u8jz3wmn">
            <Calendar className="w-3 h-3" data-id="element-661" data-mp="3app9v7nxorqs" />
            Date
          </label>
          <input type="date" className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-662" data-mp="2mytddx8j98d7" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-663" data-mp="2ryo0dpp6tdsz">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1.2fr_1fr_auto] gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-664" data-mp="c7johjv4epsf">
          <div data-id="element-665" data-mp="7at9kzv1y08l">Order ID</div>
          <div data-id="element-666" data-mp="1p5k7tp50yfn8">Customer</div>
          <div data-id="element-667" data-mp="1p5ag9x4c00ff">Source</div>
          <div data-id="element-668" data-mp="1p4r0aly8rm41">Status</div>
          <div data-id="element-669" data-mp="1u557y2vxku1j">Date</div>
          <div data-id="element-670" data-mp="1u5exxqgz7178">Amount</div>
          <div className="w-10" data-id="element-671" data-mp="1u6q23rec2ayv"></div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-672" data-mp="1g5nc53k7wgio">
          {filteredOrders.map(order => <button key={order.id} onClick={() => onSelectOrder(order)} className="w-full grid grid-cols-[1fr_1.5fr_1fr_1fr_1.2fr_1fr_auto] gap-4 px-6 py-5 items-center hover:bg-slate-50 transition-colors text-left group" data-id="element-673" data-mp="1l47019idg2do">
              <div className="font-mono font-bold text-blue-600" data-id="element-674" data-mp="2cxh7yiz43j8i">
                #{order.id}
              </div>

              <div data-id="element-675" data-mp="243cio3zmmfv3">
                <p className="font-semibold text-slate-900" data-id="element-676" data-mp="242jb50sufmbw">
                  {order.customer.name}
                </p>
                <p className="text-xs text-slate-500" data-id="element-677" data-mp="2rs59xn5azzjd">{order.customer.mobile}</p>
              </div>

              <div className="flex items-center gap-2" data-id="element-678" data-mp="2wkf5pjt3nttk">
                <div className={cn('flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase', order.channel === 'Aggregator' ? 'bg-orange-50 text-orange-700' : order.channel === 'Walk-in' ? 'bg-purple-50 text-purple-700' : 'bg-indigo-50 text-indigo-700')} data-id="element-679" data-mp="36fccp5kmxr76">
                  {order.channel === 'Aggregator' ? <Smartphone className="h-3 w-3" data-id="element-680" data-mp="2riv4gn7qssou" /> : <Globe className="h-3 w-3" data-id="element-681" data-mp="2rhtoc4bvdg09" />}
                  {order.channel}
                </div>
              </div>

              <div data-id="element-682" data-mp="2niukkn1vjpae">
                <StatusBadge status={order.status} data-id="element-683" data-mp="2iqbai02ebcac" />
              </div>

              <div className="text-sm text-slate-600 font-medium" data-id="element-684" data-mp="2inyl51ub5zn9">
                {order.createdAt}
              </div>

              <div data-id="element-685" data-mp="28rvfhm08zivk">
                <p className="font-bold text-slate-900" data-id="element-686" data-mp="28sn2w9joyaar">
                  {order.totalAmount.toFixed(2)} SAR
                </p>
                <p className={cn('text-[10px] font-bold uppercase', order.paymentType === 'COD' ? 'text-amber-600' : 'text-emerald-600')} data-id="element-687" data-mp="23s94zkbt0gqg">
                  {order.paymentType}
                </p>
              </div>

              <div className="flex items-center gap-3" data-id="element-688" data-mp="oa2voywpon60">
                <div className="text-slate-300 group-hover:text-blue-600 transition-colors" data-id="element-689" data-mp="oam9c3fbwpe8">
                  <ChevronRight className="w-5 h-5" data-id="element-690" data-mp="t84my74ajssk" />
                </div>
              </div>
            </button>)}
        </div>
      </div>
    </div>;
}
function StatusBadge({
  status
}: {
  status: OrderStatus;
}) {
  const styles: Record<string, string> = {
    Pending: 'bg-blue-50 text-blue-700 border-blue-100',
    Accepted: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    Picking: 'bg-amber-50 text-amber-700 border-amber-100',
    'Searching for Driver': 'bg-sky-50 text-sky-700 border-sky-100',
    'Awaiting Partner Courier': 'bg-cyan-50 text-cyan-700 border-cyan-100',
    'Driver Assigned': 'bg-violet-50 text-violet-700 border-violet-100',
    'Out for Delivery': 'bg-orange-50 text-orange-700 border-orange-100',
    Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    'Delivery Attempt Failed': 'bg-rose-50 text-rose-700 border-rose-100',
    Held: 'bg-slate-100 text-slate-700 border-slate-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-100',
    Rejected: 'bg-red-50 text-red-700 border-red-100',
    Reassigned: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
    'Awaiting Payment': 'bg-amber-50 text-amber-700 border-amber-100',
    'Ready for Pickup': 'bg-purple-50 text-purple-700 border-purple-100',
    'Return Requested': 'bg-rose-50 text-rose-700 border-rose-100',
    'Waiting Return Confirmation': 'bg-amber-50 text-amber-700 border-amber-100',
    Refunded: 'bg-slate-50 text-slate-700 border-slate-100',
    'Returned to Stock': 'bg-teal-50 text-teal-700 border-teal-100'
  };
  return <div className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border', styles[status] || 'bg-slate-50 text-slate-700 border-slate-100')} data-id="element-691" data-mp="3434oo016efmn">
      {status}
    </div>;
}